import { createSlice } from '@reduxjs/toolkit'
import { deterministicGuid } from 'apps/builder/lib/hash'
import { ensureNumber } from 'apps/studio/store/reducers/entities/pdf'
import {
  updateEntry,
  copyEntry,
  loadFormData,
} from 'apps/formsCreationKit/store/actions/sharedActions'
import {
  createElement,
  updateElement,
  deleteElement,
  addPdf,
  resetPdf,
  replacePdf,
  createTable,
  moveTable,
  deleteTable,
  addTableColumn,
  deleteTableColumn,
} from 'apps/studio/store/actions/sharedActions'

import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'

// const elementDefaults = {
//   id: null,
//   backgroundColor: '#FFFFFF',
//   backgroundColorOpacity: 0,
//   horizontalAlignment: 'left',
//   page_id: null,
//   entry_guid: null,
//   element_type: null,
//   styles: {},
//   position: {}
// }

export const initialState = []

// Replace all header elements with the renumbered set
const replaceHeaderElements = (state, { payload: { layout, headerElements } }) => {
  return layout?.id && headerElements
    ? [...state.filter(el => el.layout_id !== layout.id), ...headerElements]
    : state
}
export const elementsSliceExtraReducers = builder => {
  builder
    .addCase(loadFormData, (state, action) => {
      const { elements } = action.payload
      return elements
    })
    .addCase(createElement, (state, action) => {
      const { element, entry } = action.payload
      state.push({
        ...element,
        element_type: entry.type,
      })
    })
    .addCase(createTable, (state, action) => {
      const { elements = [] } = action.payload
      return elements.length ? [...state, ...elements] : state
    })
    .addCase(moveTable, (state, action) => {
      const { layout, toPage } = action.payload
      if (!layout || !toPage) return state
      return state.map(el =>
        el.layout_id === layout.id
          ? { ...el, page_id: toPage.id, page_number: toPage.page_number }
          : el
      )
    })
    .addCase(updateElement, (state, action) => {
      const { element: updatedElement } = action.payload
      return state.map(element => {
        if (element.entry_guid === updatedElement.entry_guid) {
          return {
            ...element,
            ...updatedElement,
          }
        }
        return element
      })
    })
    .addCase(deleteElement, (state, action) => {
      const { element: elementToDelete } = action.payload
      const { entry_guid } = elementToDelete
      return state.filter(element => element.entry_guid !== entry_guid)
    })
    .addCase(deleteTable, (state, action) => {
      const { layout } = action.payload
      if (!layout?.id) return state
      return state.filter(el => el.layout_id !== layout.id)
    })
    .addCase(addTableColumn, replaceHeaderElements)
    .addCase(deleteTableColumn, replaceHeaderElements)
    .addCase(addPdf, () => [])
    .addCase(resetPdf, () => [])
    .addCase(replacePdf, (state, action) => {
      const { newPageCount, newPageDimensions } = action.payload

      const survivors = state.filter(el => el.page_number <= newPageCount)

      return survivors.map(el => {
        const pageDim = newPageDimensions.find(
          d => d.pageNumber === el.page_number
        )
        if (!pageDim) return el

        const { originalWidth, originalHeight } = pageDim
        const { x = 0, y = 0, width = 0, height = 0 } = el.position || {}

        return {
          ...el,
          position: {
            ...el.position,
            x: Math.min(x, Math.max(0, originalWidth - width)),
            y: Math.min(y, Math.max(0, originalHeight - height)),
          },
        }
      })
    })
    .addCase(updateEntry, (state, action) => {
      const { guid, type: updatedEntryType } = action.payload

      if (!updatedEntryType) return state

      return state.map(element => {
        if (element.entry_guid === guid) {
          return {
            ...element,
            element_type: updatedEntryType,
          }
        }
        return element
      })
    })
    .addCase(copyEntry, (state, action) => {
      const { guid, seed, maxWidth = 0, maxHeight = 0 } = action.payload
      const originalElementIndex = state.findIndex(
        ({ entry_guid }) => entry_guid === guid
      )
      const originalElement = state[originalElementIndex]

      // original element's position
      const { x: currentX = 0, y: currentY = 0 } =
        originalElement?.position || {}

      // Test if adding 7px will exceed the page dimensions
      const wouldExceedWidth = currentX + 7 > maxWidth
      const wouldExceedHeight = currentY + 7 > maxHeight

      let newX = currentX + 7,
        newY = currentY + 7

      if (wouldExceedWidth && wouldExceedHeight) {
        // If both changes would be outside, move only slightly or not at all
        newX = currentX + Math.min(7, Math.max(0, maxWidth - currentX))
        newY = currentY + Math.min(7, Math.max(0, maxHeight - currentY))
      } else if (wouldExceedWidth) {
        // only X is over, so subtract 7 from X
        newX = Math.max(0, currentX - 7)
      } else if (wouldExceedHeight) {
        // only Y is over, so subtract 7 from Y
        newY = Math.max(0, currentY - 7)
      }

      const copiedElement = {
        ...originalElement,
        entry_guid: deterministicGuid(guid, seed),
        position: {
          ...originalElement.position,
          x: newX,
          y: newY,
        },
      }

      return [
        ...state.slice(0, originalElementIndex + 1),
        copiedElement,
        ...state.slice(originalElementIndex + 1),
      ]
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { pdf } = action.payload
      const { elements } = pdf
      return elements
    })
}

export const elementsSliceConfig = {
  name: 'elements',
  initialState: initialState,
  reducers: {
    updateElementStyles(state, action) {
      const { entry_guid, ...styles } = action.payload
      return state.map(element => {
        if (element.entry_guid === entry_guid) {
          return {
            ...element,
            styles: {
              ...element.styles,
              ...styles,
            },
          }
        }
        return element
      })
    },
    updateElementPosition(state, action) {
      const { entry_guid, ...position } = action.payload
      return state.map(element => {
        if (element.entry_guid === entry_guid) {
          return {
            ...element,
            position: {
              ...element.position,
              ...ensureNumber(position),
            },
          }
        }
        return element
      })
    },
  },
  extraReducers: elementsSliceExtraReducers,
}

export const elementsSlice = createSlice(elementsSliceConfig)
export const { actions: elementsActions, reducer: elementsReducer } =
  elementsSlice
